import {
  Color,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Texture,
  Vector2,
} from 'three';
import {checkExists} from 'external/dev_april_corgi+/js/common/asserts';

import {Z_TEXT} from './z';

// We render at 4x for crispness
const RETINA = 4;

export class TextMesh extends Mesh {
  static async empty(): Promise<TextMesh> {
    return new TextMesh(await createImageBitmap(new ImageData(1, 1)));
  }

  constructor(bitmap: ImageBitmap) {
    super(
      new PlaneGeometry(bitmap.width / RETINA, bitmap.height / RETINA),
      new MeshBasicMaterial({
        color: new Color(0xffffff),
        map: new Texture(bitmap),
        transparent: true,
      }),
    );
    this.geometry.computeBoundingBox();
    checkExists((this.material as MeshBasicMaterial).map).needsUpdate = true;
  }

  set size(size: Vector2) {
    this.geometry.dispose();
    this.geometry = new PlaneGeometry(size.x, size.y);
  }
}

export class TextRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly context: CanvasRenderingContext2D;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.context = checkExists(this.canvas.getContext('2d'));
  }

  async render(
    text: string,
    options?: {
      color?: string;
      name?: string;
      scale?: number;
      visible?: boolean;
    },
  ): Promise<TextMesh> {
    if (!text) {
      return await TextMesh.empty();
    }

    const color = options?.color ?? '#fafafa';
    const scale = options?.scale ?? 1;
    const mesh = new TextMesh(await this.renderBitmap(text, color, scale));
    mesh.position.z = Z_TEXT;
    mesh.visible = options?.visible ?? true;
    if (options?.name) {
      mesh.name = options.name;
    }
    return mesh;
  }

  private renderBitmap(
    text: string,
    color: string,
    scale: number,
  ): Promise<ImageBitmap> {
    const canvas = this.canvas;
    const context = this.context;
    setupContext(color, scale, context);
    const metrics = context.measureText(text);
    const width =
      metrics.actualBoundingBoxRight - metrics.actualBoundingBoxLeft;
    if (width > 0) {
      canvas.width = width;
    } else {
      canvas.width = metrics.width;
    }
    canvas.height =
      metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
    setupContext(color, scale, context);
    context.fillText(text, 0, canvas.height / 2);

    return createImageBitmap(canvas, {imageOrientation: 'flipY'});
  }
}

export function capitalize(id: string): string {
  return id.charAt(0).toUpperCase() + id.slice(1).replaceAll('_', ' ');
}

function setupContext(
  color: string,
  scale: number,
  context: CanvasRenderingContext2D,
): void {
  const font = `${15 * scale * RETINA}px ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"`;
  const letterSpacing = '0.5px';
  context.font = font;
  context.fillStyle = color;
  context.letterSpacing = letterSpacing;
  context.textBaseline = 'middle';
}
